import Add from "@mui/icons-material/Add";
import { Box, Popover, Stack, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import TagPickerPanel from "../../../tags/TagPickerPanel";
import { TagChip } from "../TagChip";

// ─────────────────────────────────────────────────────────────────────────────
// CoAppInlineTagsCell — per-row inline tag editor for data-grid cells.
//
// Renders the row's existing tags as uniform-teal TagChips with × removers,
// followed by a dashed "+" button. Clicking "+" opens the same TagPickerPanel
// that the header "Manage Tags" button uses (so create / rename / delete look
// identical in both surfaces). Selecting a tag in the popover calls onAddTag.
//
// The cell is fully controlled by the parent — it never mutates the row's
// tags itself. Parents pass `onAddTag(tag)` / `onRemoveTag(tag)` callbacks
// that know how to persist the change for that resource.
// ─────────────────────────────────────────────────────────────────────────────

export interface InlineTag {
	id: number | string;
	name: string;
}

interface CoAppInlineTagsCellProps {
	tags: InlineTag[];
	onAddTag: (tag: InlineTag) => void;
	onRemoveTag: (tag: InlineTag) => void;
	disabled?: boolean;
}

export default function CoAppInlineTagsCell({
	tags,
	onAddTag,
	onRemoveTag,
	disabled = false
}: CoAppInlineTagsCellProps) {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const open = Boolean(anchorEl);

	const safeTags = Array.isArray(tags) ? tags : [];
	const appliedIds = useMemo(() => new Set(safeTags.map((t) => String(t.id))), [safeTags]);

	// Show at most this many chips inline; the rest collapse into a single
	// circular "+N" counter (capped at "9+") with the remainder in a tooltip.
	const MAX_VISIBLE_TAGS = 2;
	const visibleTags = safeTags.slice(0, MAX_VISIBLE_TAGS);
	const overflowTags = safeTags.slice(MAX_VISIBLE_TAGS);
	const overflowCount = overflowTags.length;
	const overflowLabel = overflowCount > 9 ? "9+" : String(overflowCount);

	const handleAdd = (tag: InlineTag) => {
		onAddTag(tag);
	};

	return (
		<Stack
			component="div"
			direction="row"
			spacing={0.5}
			sx={{
				alignItems: "center",
				justifyContent: safeTags.length === 0 ? "center" : "flex-start",
				width: "100%",
				height: "100%",
				gap: 0.5,
				flexWrap: "nowrap",
				overflow: "hidden"
			}}
		>
			{visibleTags.map((tag) => (
				<TagChip
					key={tag.id}
					label={tag.name}
					onRemove={disabled ? undefined : () => onRemoveTag(tag)}
				/>
			))}
			{overflowCount > 0 && (
				<Tooltip
					arrow
					title={
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.25 }}>
							{overflowTags.map((tag) => (
								<Typography key={tag.id} variant="caption" sx={{ color: "#FFFFFF", lineHeight: 1.5 }}>
									{tag.name}
								</Typography>
							))}
						</Box>
					}
				>
					<Box
						component="span"
						aria-label={`${overflowCount} more tag${overflowCount > 1 ? "s" : ""}`}
						sx={{
							flex: "0 0 auto",
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							minWidth: 22,
							height: 22,
							px: 0.5,
							borderRadius: 999,
							fontSize: 11,
							fontWeight: 700,
							lineHeight: 1,
							color: "#02B894",
							background: "rgba(2, 213, 171, 0.12)",
							border: "1px solid rgba(2, 213, 171, 0.32)",
							cursor: "default",
							userSelect: "none"
						}}
					>
						{overflowLabel}
					</Box>
				</Tooltip>
			)}
			{!disabled && (
				<Box
					component="button"
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
						e.stopPropagation();
						setAnchorEl(e.currentTarget);
					}}
					aria-label="Add tag"
					sx={{
						all: "unset",
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 22,
						height: 22,
						borderRadius: 999,
						border: "1.5px dashed rgba(13, 31, 48, 0.30)",
						color: "rgba(13, 31, 48, 0.55)",
						cursor: "pointer",
						transition: "border-color 0.18s ease, color 0.18s ease, background 0.18s ease",
						"&:hover": {
							borderColor: "#02D5AB",
							color: "#02D5AB",
							background: "rgba(2, 213, 171, 0.08)"
						}
					}}
				>
					<Add sx={{ fontSize: 14 }} />
				</Box>
			)}
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				slotProps={{
					paper: {
						sx: {
							mt: 0.75,
							borderRadius: 2.5,
							border: "1px solid rgba(13, 31, 48, 0.06)",
							boxShadow: "0 12px 30px rgba(13, 31, 48, 0.14), 0 2px 8px rgba(13, 31, 48, 0.06)",
							overflow: "hidden"
						}
					}
				}}
			>
				<TagPickerPanel
					mode="select"
					appliedTagIds={appliedIds}
					onAddTag={(tag) => handleAdd(tag as InlineTag)}
					dense
				/>
			</Popover>
		</Stack>
	);
}
